import { InspectionTemplate } from "@/shared/models/Inspection";
import {
	Autocomplete,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";

type Props = {
	templates: InspectionTemplate[];
	sectionId: number | null;
	sectionName: string;
	onClose: () => void;
	onClone: (template: InspectionTemplate, withDrawing: boolean) => void;
};

export const CloneSectionDialog = ({
	templates,
	sectionId,
	sectionName,
	onClose,
	onClone,
}: Props) => {
	const [templateId, setTemplateId] = useState<number | null>(null);
	const [withDrawing, setWithDrawing] = useState(true);

	const sources = templates.filter(
		(template) => template.IsCurrentRev && template.SectionId !== sectionId,
	);
	const source =
		sources.find((template) => template.TemplateId === templateId) ?? null;

	return (
		<Dialog
			open
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			data-test-id="clone-section-dialog"
		>
			<DialogTitle>Clone from another section</DialogTitle>
			<DialogContent>
				<DialogContentText sx={{ mb: 2 }}>
					{`The sheet that section is inspected with is written onto ${sectionName} as a new draft. Nothing of the section it came from is touched, and the two sheets go their own way from here.`}
				</DialogContentText>
				<Autocomplete
					options={sources}
					value={source}
					size="small"
					autoHighlight
					openOnFocus
					getOptionLabel={(template) =>
						`${template.SectionName} · Rev ${template.Rev}`
					}
					isOptionEqualToValue={(option, item) =>
						option.TemplateId === item.TemplateId
					}
					onChange={(_event, template) =>
						setTemplateId(template?.TemplateId ?? null)
					}
					renderOption={(props, template) => (
						<li {...props} key={template.TemplateId}>
							<Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
								{template.SectionName}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{`Rev ${template.Rev} · ${template.ColumnCount} columns${
									template.UploadFileId === null ? "" : " · drawing"
								}`}
							</Typography>
						</li>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Section to clone from"
							size="small"
							autoFocus
							placeholder="Type a section"
							slotProps={{
								htmlInput: {
									...params.inputProps,
									"data-test-id": "clone-source-input",
								},
							}}
						/>
					)}
				/>
				{source?.UploadFileId != null && (
					<FormControlLabel
						sx={{ mt: 1 }}
						control={
							<Checkbox
								size="small"
								checked={withDrawing}
								onChange={(event) => setWithDrawing(event.target.checked)}
							/>
						}
						label={
							<Typography variant="body2">Bring the drawing along</Typography>
						}
					/>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					color="inherit"
					onClick={onClose}
					data-test-id="cancel-clone-button"
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={source === null}
					data-test-id="clone-button"
					onClick={() =>
						source !== null &&
						onClone(source, withDrawing && source.UploadFileId !== null)
					}
				>
					Clone
				</Button>
			</DialogActions>
		</Dialog>
	);
};
